import Alert from '@mui/material/Alert';
import {alpha, styled} from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {hideAlert} from 'redux/actions';

const PREFIX = 'AppMessageView';

const classes = {
  success: `${PREFIX}-success`,
  error: `${PREFIX}-error`,
  info: `${PREFIX}-info`,
  warning: `${PREFIX}-warning`,
  icon: `${PREFIX}-icon`,
  iconVariant: `${PREFIX}-iconVariant`,
  message: `${PREFIX}-message`,
};

const StyledAlertBar = styled(Alert)(({theme}) => ({
  // Use Poppins from your theme for consistent typography
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  fontSize: 14,
  // Use your STANDARD (4px) radius for that "Machined" look
  borderRadius: theme.sharpRadius || 4,
  color: '#FFFFFF', // High contrast white for "Surgical" legibility
  transition: 'all 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',

  // Specific severity branding
  '&.MuiAlert-filledSuccess': {
    backgroundColor: theme.palette.success.main, // green[600]
    boxShadow: '0px 4px 12px rgba(0, 140, 69, 0.2)', // Subtle green glow
    color: theme.palette.success.contrastText,
  },

  '&.MuiAlert-filledError': {
    backgroundColor: theme.palette.error.main, // YAMAN_ERROR (#d32f2f)
    boxShadow: '0px 4px 12px rgba(211, 47, 47, 0.2)', // Subtle red glow
  },

  '&.MuiAlert-filledInfo': {
    backgroundColor: theme.palette.primary.main, // YAMAN_GOLD (#D4AF37)
  },

  '&.MuiAlert-filledWarning': {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    // Add the dark border for the "Executive" look
    border: `1px solid ${theme.palette.warning.dark}`,
    boxShadow: '0px 4px 12px rgba(198, 99, 0, 0.15)',
  },

  '&.MuiAlert-standardWarning': {
    backgroundColor: theme.palette.warning.light, // #FFECDC
    color: theme.palette.warning.dark, // Use dark for better text contrast
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  },

  [`& .MuiAlert-action`]: {
    fontSize: 20,
    '& .MuiIconButton-root': {
      color: '#FFFFFF', // Force the "X" to white
    },
  },
  [`& .${classes.icon}`]: {
    fontSize: 20,
    color: '#FFFFFF',
  },

  [`& .${classes.message}`]: {
    display: 'flex',
    alignItems: 'center',
    letterSpacing: '0.5px', // Matches your executive theme feel
  },
}));

const AppAlertBar = ({message, type}) => {
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();
  const onClose = () => {
    setOpen(false);
    setTimeout(() => dispatch(hideAlert()), 1000);
  };
  useEffect(() => {
    if (message) {
      setOpen(true);
    }
  }, [message]);

  return (
    <StyledAlertBar
      variant='filled'
      severity={type}
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 9999,
        borderRadius: 0, // Sharp "Instrument" style
        transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
      }}
    >
      <span id='client-snackbar' className={classes.message}>
        {message}
      </span>
    </StyledAlertBar>
  );
};

AppAlertBar.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['error', 'info', 'success', 'warning']).isRequired,
};

export default AppAlertBar;
